import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Cpu, Zap, Shield, Brain, ArrowRight, CheckCircle } from 'lucide-react';
import { MainNavigation } from '@/components/layout/MainNavigation';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export const Technology = () => {
  return (
    <>
      <Helmet>
        <title>AI-Powered PEO Matching Technology | The PEO App</title>
        <meta name="description" content="Discover our patent-pending AI technology that revolutionizes PEO matching, delivering 83% faster results with unmatched accuracy and precision." />
        <meta name="keywords" content="PEO technology, AI matching, patent pending, machine learning, automated PEO selection" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-24 bg-gradient-to-br from-primary/5 to-secondary/5">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Brain className="h-4 w-4" />
                  Patent Pending Technology
                </div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6">
                  Revolutionary AI-Powered
                  <span className="text-primary block">PEO Matching</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                  Our patent-pending artificial intelligence technology transforms the PEO selection process, 
                  delivering precise matches 83% faster than traditional methods.
                </p>
                <Button asChild size="lg" className="mr-4">
                  <Link to="/signup">
                    Experience the Technology
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </section>

          {/* Technology Overview */}
          <section className="py-24">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-bold mb-6">How Our AI Technology Works</h2>
                  <p className="text-muted-foreground mb-6">
                    Our proprietary machine learning algorithms analyze hundreds of variables to create 
                    precise PEO matches tailored to your business needs, industry requirements, and growth objectives.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Multi-Variable Analysis</h4>
                        <p className="text-sm text-muted-foreground">Processes 200+ data points including industry, size, location, and specific needs</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Predictive Matching</h4>
                        <p className="text-sm text-muted-foreground">AI predicts compatibility scores based on historical success patterns</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Continuous Learning</h4>
                        <p className="text-sm text-muted-foreground">System improves accuracy with every successful match and client feedback</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-primary/10 to-secondary/10 p-8 rounded-2xl"
                >
                  <div className="text-center">
                    <Cpu className="h-16 w-16 text-primary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-4">83% Faster Results</h3>
                    <p className="text-muted-foreground">
                      Traditional PEO shopping takes weeks. Our AI delivers qualified matches in minutes.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Technology Features */}
          <section className="py-24 bg-muted/30">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl font-bold mb-4">Advanced Technology Features</h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  Built with cutting-edge AI and machine learning capabilities to deliver unparalleled accuracy and speed.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="bg-background rounded-xl p-6 shadow-sm border"
                >
                  <Brain className="h-10 w-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">Machine Learning Engine</h3>
                  <p className="text-muted-foreground">
                    Neural networks trained on thousands of successful PEO partnerships to identify optimal matches.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="bg-background rounded-xl p-6 shadow-sm border"
                >
                  <Zap className="h-10 w-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">Real-Time Processing</h3>
                  <p className="text-muted-foreground">
                    Instant analysis and matching with sub-second response times for immediate results.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="bg-background rounded-xl p-6 shadow-sm border"
                >
                  <Shield className="h-10 w-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">Secure Architecture</h3>
                  <p className="text-muted-foreground">
                    Enterprise-grade security with HIPAA and SOC II compliance built into every layer.
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Patent Information */}
          <section className="py-24">
            <div className="container mx-auto px-4">
              <div className="bg-gradient-to-r from-primary to-secondary text-white rounded-2xl p-12 text-center max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-bold mb-6">Patent-Pending Innovation</h2>
                  <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                    Our unique AI-driven matching algorithm is protected by patent-pending status, 
                    representing a breakthrough in HR technology and PEO selection methodology.
                  </p>
                  <Button variant="secondary" size="lg" asChild>
                    <Link to="/signup">
                      Get Started Today
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </motion.div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Technology;